/** Loading / error / empty / unconfigured building blocks. Honest by design:
 *  no fabricated placeholder numbers, ever. */

import type { ReactNode } from 'react'
import type { QueryState } from '../../api/hooks'

export function Spinner({ label = 'loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-crt-dim">
      <span className="inline-block h-3 w-3 animate-pulse bg-crt-lime" />
      <span className="cursor-blink text-xs uppercase tracking-widest">
        {label}
      </span>
    </div>
  )
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="border border-crt-neg bg-crt-panel p-4">
      <div className="text-xs uppercase tracking-widest text-crt-neg">
        gateway error
      </div>
      <div className="mt-1 break-all text-sm text-crt-gray">{message}</div>
      {onRetry ? (
        <button type="button" className="btn-ghost mt-3" onClick={onRetry}>
          retry
        </button>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  detail,
}: {
  title: string
  detail?: string
}) {
  return (
    <div className="p-6 text-center">
      <div className="text-sm uppercase tracking-widest text-crt-dim">
        {title}
      </div>
      {detail ? (
        <div className="mx-auto mt-2 max-w-md text-xs text-crt-dim">
          {detail}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Render helper: collapses loading/error/ready into one place per section.
 * "No data yet" arrives as null fields inside a 200 payload (e.g.
 * `{state: null}`) — pages render those with <EmptyState> and honest copy.
 */
export function QueryView<T>({
  state,
  onRetry,
  children,
}: {
  state: QueryState<T>
  onRetry?: () => void
  children: (data: T) => ReactNode
}) {
  switch (state.kind) {
    case 'loading':
      return <Spinner />
    case 'error':
      return <ErrorState message={state.message} onRetry={onRetry} />
    case 'ready':
      return <>{children(state.data)}</>
  }
}
